import React, { useEffect, useState } from "react";

/** Shown on the button immediately while auth is in flight. */
export const AUTH_CONNECTING_INITIAL = "Connecting…";

/**
 * Rotating copy after a short delay — cold starts / sleeping compute.
 * Keep tone warm, clear, and sustainability-aware without sounding broken.
 */
export const AUTH_WARMUP_MESSAGES = [
  {
    title: "Waking a sleeping server",
    body: "We power down quiet capacity between visits to keep our energy footprint light.",
  },
  {
    title: "Stretching the cloud awake",
    body: "A little patience now means less always-on hardware humming in the background.",
  },
  {
    title: "Warming the architecture brain",
    body: "Your map is worth the wait — we’re bringing just enough compute online.",
  },
  {
    title: "Still with you",
    body: "Sustainable hosting sometimes starts from a nap. We’re nearly ready.",
  },
  {
    title: "Connecting the dots",
    body: "Thanks for choosing a lighter footprint — we’ll have you signed in shortly.",
  },
  {
    title: "Almost there",
    body: "Green compute takes a moment to stretch. Hang tight.",
  },
];

const SHOW_AFTER_MS = 2000;
const ROTATE_EVERY_MS = 3500;

/**
 * Engaging status panel while login / SSO is waiting on a cold or sleepy backend.
 */
export default function AuthConnectingStatus({
  active = false,
  showAfterMs = SHOW_AFTER_MS,
  rotateEveryMs = ROTATE_EVERY_MS,
}) {
  const [visible, setVisible] = useState(false);
  const [index, setIndex] = useState(0);
  const [fadeKey, setFadeKey] = useState(0);

  useEffect(() => {
    if (!active) {
      setVisible(false);
      setIndex(0);
      setFadeKey(0);
      return undefined;
    }

    const showTimer = window.setTimeout(() => setVisible(true), showAfterMs);
    return () => window.clearTimeout(showTimer);
  }, [active, showAfterMs]);

  useEffect(() => {
    if (!active || !visible) return undefined;

    const rotateTimer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % AUTH_WARMUP_MESSAGES.length);
      setFadeKey((k) => k + 1);
    }, rotateEveryMs);

    return () => window.clearInterval(rotateTimer);
  }, [active, visible, rotateEveryMs]);

  if (!active || !visible) return null;

  const message = AUTH_WARMUP_MESSAGES[index] || AUTH_WARMUP_MESSAGES[0];

  return (
    <div
      className="rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-3 text-left shadow-sm"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center">
            <span className="ui-loading-spinner ui-loading-spinner-sm" aria-hidden="true" />
          </span>
        <div key={fadeKey} className="min-w-0 auth-warmup-fade">
          <p className="text-sm font-semibold text-cyan-950">{message.title}</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-700">{message.body}</p>
        </div>
      </div>
    </div>
  );
}
